// Your call Y10: an AI result saved as a file carries a metadata marker, not only its `_ai-` filename (a rename
// loses that). The marker is the IPTC standard one, read by Photoshop, Lightroom and the big photo sites:
// Iptc4xmpExt:DigitalSourceType = trainedAlgorithmicMedia, in an XMP packet. The provider's bytes are kept as
// they are (including any C2PA data it adds); the packet is only inserted. Pure; unit-tested.
import { chunk } from '../../io/encode/png';
import { XMP_HEADER } from '../../io/encode/jpeg';

export const AI_SOURCE_TYPE = 'http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia';

export function aiXmp(kind: 'touchup' | 'upscale'): string {
  return (
    `<?xpacket begin="\uFEFF" id="W5M0MpCehiHzreSzNTczkc9d"?>` +
    `<x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">` +
    `<rdf:Description rdf:about="" xmlns:Iptc4xmpExt="http://iptc.org/std/Iptc4xmpExt/2008-02-29/" xmlns:xmp="http://ns.adobe.com/xap/1.0/">` +
    `<Iptc4xmpExt:DigitalSourceType>${AI_SOURCE_TYPE}</Iptc4xmpExt:DigitalSourceType>` +
    `<xmp:CreatorTool>EAT Photo Booth (AI ${kind})</xmp:CreatorTool>` +
    `</rdf:Description></rdf:RDF></x:xmpmeta><?xpacket end="w"?>`
  );
}

const isPng = (b: Uint8Array) => b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47;
const isJpeg = (b: Uint8Array) => b[0] === 0xff && b[1] === 0xd8;

/** The same image with the AI marker added; any other format is returned unchanged. */
export function markAiGenerated(bytes: Uint8Array, kind: 'touchup' | 'upscale'): Uint8Array {
  const xmp = new TextEncoder().encode(aiXmp(kind));
  if (isPng(bytes)) {
    // iTXt "XML:com.adobe.xmp", uncompressed, right after IHDR (8-byte signature + 25-byte IHDR chunk).
    const kw = Uint8Array.from('XML:com.adobe.xmp', (c) => c.charCodeAt(0));
    const body = new Uint8Array(kw.length + 5 + xmp.length);
    body.set(kw, 0);
    body.set(xmp, kw.length + 5);
    const ihdrEnd = 8 + 12 + ((bytes[8]! << 24) | (bytes[9]! << 16) | (bytes[10]! << 8) | bytes[11]!);
    return concat([bytes.subarray(0, ihdrEnd), chunk('iTXt', body), bytes.subarray(ihdrEnd)]);
  }
  if (isJpeg(bytes)) {
    const head = Uint8Array.from(XMP_HEADER, (c) => c.charCodeAt(0));
    const len = 2 + head.length + xmp.length;
    if (len > 0xffff) return bytes;
    const seg = concat([new Uint8Array([0xff, 0xe1, len >> 8, len & 255]), head, xmp]);
    // After SOI and an APP0 JFIF segment when there is one (JFIF must come first).
    let at = 2;
    if (bytes[2] === 0xff && bytes[3] === 0xe0) at = 4 + ((bytes[4]! << 8) | bytes[5]!);
    return concat([bytes.subarray(0, at), seg, bytes.subarray(at)]);
  }
  return bytes;
}

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}
